import { Sliders02Icon } from '@langchain/untitled-ui-icons';

import { cn } from '@/utils/tailwind';

export const enum Step {
  BasicConfig,
  AlgorithmConfig,
}

export const StepText = {
  [Step.BasicConfig]: 'Configure',
  [Step.AlgorithmConfig]: 'Set up data',
};

enum StepState {
  ToDo,
  InProgress,
  Done,
}

const ProgressCircle = ({ state }: { state: StepState }) => {
  return (
    <div
      className={cn(
        'my-auto flex items-center justify-center',
        state === StepState.InProgress &&
          'h-fit rounded-full border border-brand',
        state === StepState.ToDo && 'opacity-70'
      )}
    >
      <span
        className={cn(
          'm-px h-3 w-3 rounded-full border border-gray-800',
          state === StepState.Done ? 'bg-brand-primary' : 'bg-background'
        )}
      />
    </div>
  );
};

const ProgressText = ({ state, text }: { state: StepState; text: string }) => {
  return (
    <div
      className={cn(
        'my-auto text-sm',
        state === StepState.InProgress && 'font-medium',
        state === StepState.ToDo && 'opacity-70'
      )}
    >
      {text}
    </div>
  );
};

const ProgressStep = ({
  step,
  setStep,
  state,
}: {
  step: Step;
  setStep: (step: Step) => void;
  state: StepState;
}) => {
  return (
    <button
      type="button"
      onClick={() => setStep(step)}
      className="flex flex-row gap-2"
    >
      <ProgressCircle state={state} />
      <ProgressText state={state} text={StepText[step]} />
    </button>
  );
};

const ProgressBar = ({
  step,
  setStep,
}: {
  step: Step;
  setStep: (step: Step) => void;
}) => {
  return (
    <div className="flex flex-row gap-2">
      <ProgressStep
        step={Step.BasicConfig}
        setStep={setStep}
        state={
          Step.BasicConfig === step ? StepState.InProgress : StepState.Done
        }
      />
      <div className="my-auto w-6 border border-primary" />
      <ProgressStep
        step={Step.AlgorithmConfig}
        setStep={setStep}
        state={
          Step.AlgorithmConfig === step ? StepState.InProgress : StepState.ToDo
        }
      />
    </div>
  );
};

export const ConfigureOptimizeLayout = ({
  children,
  step,
  setStep,
}: {
  children: React.ReactNode;
  step: Step;
  setStep: (step: Step) => void;
}) => {
  return (
    <div className="flex w-[700px] flex-col gap-6 overflow-y-auto">
      <div className="flex flex-row gap-2">
        <div className="mb-auto rounded-lg border border-gray-100 p-3 shadow-sm">
          <Sliders02Icon className="h-6 w-6" />
        </div>
        <div className="flex flex-col">
          <span className="text-xl font-medium">Set up Prompt Optimizer</span>
          <span className="text-sm text-tertiary">
            Configure your optimization settings
          </span>
        </div>
      </div>
      <ProgressBar step={step} setStep={setStep} />
      {children}
    </div>
  );
};
